import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { GlobalService, HttpService } from "../../modules";
import i18n from "../../i18n";
import { 
    MarketingPopup, 
    MarketingPopupBackendRequest,
    CreateMarketingPopupRequest, 
    UpdateMarketingPopupRequest, 
    DeleteMarketingPopupRequest,
    MarketingPopupResponse 
} from "../../modules/shared/model/MarketingPopupInterfaces";

// Get all marketing popups
export const getMarketingPopups = createAsyncThunk<MarketingPopupResponse, {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    sort?: string;
    order?: string;
} | undefined>(
    "marketingPopup/getMarketingPopups",
    async (params = {}, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            // Build query parameters
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append("page", params.page.toString());
            if (params.limit) queryParams.append("limit", params.limit.toString());
            
            // Add sort parameter in the format backend expects: array of {orderBy, order}
            if (params.sort && params.order) {
                const sortArray = [{
                    orderBy: params.sort,
                    order: params.order.toUpperCase() // Backend expects uppercase (ASC/DESC)
                }];
                queryParams.append("sort", JSON.stringify(sortArray));
            }

            const url = queryParams.toString() 
                ? `api/v1/marketing-popups?${queryParams.toString()}`
                : "api/v1/marketing-popups";

            const response = await HttpService.get(url, true, {});
            dispatch(hideLoader());

            if (typeof response == "string" && response.length == 0) {
                return {
                    data: [],
                    totalCount: 0,
                    hasNextPage: false,
                };
            }
            
            // Backend returns: { data, hasNextPage, hasPreviousPage, totalCount, page, limit }
            if (response) {
                return {
                    data: response.data || [],
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false
                };
            }
            
            return {
                data: [],
                totalCount: 0,
                hasNextPage: false,
            };
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to fetch marketing popups";

            return rejectWithValue(errorMessage);
        }
    }
);

// Get single marketing popup by ID
export const getMarketingPopupById = createAsyncThunk<MarketingPopup, { id: string }>(
    "marketingPopup/getMarketingPopupById",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.get(
                `api/v1/marketing-popups/${id}`,
                true,
                {}
            );
            dispatch(hideLoader());

            if (typeof response == "string" && response.length == 0) {
                return null;
            } else {
                return response.data;
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to fetch marketing popup";

            return rejectWithValue(errorMessage);
        }
    }
);

// Create new marketing popup
export const createMarketingPopup = createAsyncThunk<MarketingPopup, CreateMarketingPopupRequest>(
    "marketingPopup/createMarketingPopup",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            // Format the data before sending to backend
            const formattedData: any = { ...data };

            // Keep dates as strings (backend now accepts string dates)
            // No conversion needed

            // Comment out contentImage for now as requested
            // if (formattedData.contentImage) {
            //     formattedData.contentImage = { id: "1" };
            // }

            // Ensure all ID fields are properly formatted as numbers
            const idFields = [
                "designAnimation", "timingDisplayFrequency", 
                "displayLocation", "pageLocation", "categoryType", "designTheme", 
                "targetingAudience", "type"
            ];

            idFields.forEach(field => {
                if (formattedData[field] && typeof formattedData[field] === "object") {
                    const idValue = formattedData[field].id;
                    if (idValue && !isNaN(Number(idValue))) {
                        formattedData[field] = { id: Number(idValue) };
                    } else {
                        formattedData[field] = { id: 1 };
                    }
                }
            });

            // Filter out invalid language entries
            if (formattedData.targetingLanguage && Array.isArray(formattedData.targetingLanguage)) {
                // Remove entries with invalid IDs (like "1", 1, null, undefined, empty)
                formattedData.targetingLanguage = formattedData.targetingLanguage.filter((lang: any) => {
                    const id = lang?.id;
                    // Keep only valid MongoDB ObjectIds (24 character hex strings)
                    return id && typeof id === "string" && id.length === 24 && /^[a-f\d]{24}$/i.test(id);
                });
                
                // If all entries were filtered out, set to empty array
                if (formattedData.targetingLanguage.length === 0) {
                    formattedData.targetingLanguage = [];
                }
            }

            // Convert performance metrics to numbers
            if (formattedData.performanceImpressions !== undefined) {
                formattedData.performanceImpressions = Number(formattedData.performanceImpressions);
            }
            if (formattedData.performanceClicks !== undefined) {
                formattedData.performanceClicks = Number(formattedData.performanceClicks);
            }
            if (formattedData.performanceDismissals !== undefined) {
                formattedData.performanceDismissals = Number(formattedData.performanceDismissals);
            }

            const response = await HttpService.post(
                "api/v1/marketing-popups",
                formattedData,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.marketingPopup.created"));
            return response.data;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to create marketing popup";

            return rejectWithValue(errorMessage);
        }
    }
);

// Update marketing popup
export const updateMarketingPopup = createAsyncThunk<MarketingPopup, UpdateMarketingPopupRequest>(
    "marketingPopup/updateMarketingPopup",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            // Format the data before sending to backend
            const formattedData: any = { ...data };

            // Keep dates as strings (backend now accepts string dates)
            // No conversion needed

            // Comment out contentImage for now as requested
            // if (formattedData.contentImage) {
            //     formattedData.contentImage = { id: "1" };
            // }

            // Ensure all ID fields are properly formatted as numbers
            const idFields = [
                "designAnimation", "timingDisplayFrequency", 
                "displayLocation", "pageLocation", "categoryType", "designTheme", 
                "targetingAudience", "type"
            ];

            idFields.forEach(field => {
                if (formattedData[field] && typeof formattedData[field] === "object") {
                    const idValue = formattedData[field].id;
                    if (idValue && !isNaN(Number(idValue))) {
                        formattedData[field] = { id: Number(idValue) };
                    } else {
                        formattedData[field] = { id: 1 };
                    }
                }
            });

            // Filter out invalid language entries
            if (formattedData.targetingLanguage && Array.isArray(formattedData.targetingLanguage)) {
                // Remove entries with invalid IDs (like "1", 1, null, undefined, empty)
                formattedData.targetingLanguage = formattedData.targetingLanguage.filter((lang: any) => {
                    const id = lang?.id;
                    // Keep only valid MongoDB ObjectIds (24 character hex strings)
                    return id && typeof id === "string" && id.length === 24 && /^[a-f\d]{24}$/i.test(id);
                });
                
                // If all entries were filtered out, set to empty array
                if (formattedData.targetingLanguage.length === 0) {
                    formattedData.targetingLanguage = [];
                }
            }

            // Convert performance metrics to numbers
            if (formattedData.performanceImpressions !== undefined) {
                formattedData.performanceImpressions = Number(formattedData.performanceImpressions);
            }
            if (formattedData.performanceClicks !== undefined) {
                formattedData.performanceClicks = Number(formattedData.performanceClicks);
            }
            if (formattedData.performanceDismissals !== undefined) {
                formattedData.performanceDismissals = Number(formattedData.performanceDismissals);
            }
            
            const response = await HttpService.patch(
                `api/v1/marketing-popups/${id}`,
                formattedData,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.marketingPopup.updated"));
            return response.data;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to update marketing popup";

            return rejectWithValue(errorMessage);
        }
    }
);

// Delete marketing popup
export const deleteMarketingPopup = createAsyncThunk<{ id: string }, DeleteMarketingPopupRequest>(
    "marketingPopup/deleteMarketingPopup",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/marketing-popups/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());
            
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.marketingPopup.deleted"));
            return { id };
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to delete marketing popup";

            return rejectWithValue(errorMessage);
        }
    }
);

// Update marketing popup status
export const updateMarketingPopupStatus = createAsyncThunk<MarketingPopup, { id: string; status: string }>(
    "marketingPopup/updateMarketingPopupStatus",
    async ({ id, status }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const response = await HttpService.patch(
                `api/v1/marketing-popups/${id}/status`,
                { status },
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.marketingPopup.statusUpdated"));
            return response.data;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to update marketing popup status";

            return rejectWithValue(errorMessage);
        }
    }
);
